import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, Injector, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { PermissionService } from '../services/permission.service';
import { ThemeService } from '../services/theme.service';
import { environment } from '../../../environments/environment';
import { BrandingApiService } from './branding-api.service';
import {
	BrandingConfig,
	ResolvedBranding,
	buildBrandingCss,
	resolveBranding,
	resolveBrandingAssetUrl,
} from './branding.models';

export const BRANDING_STYLE_ID = 'tenant-branding';
export const BRANDING_PREVIEW_PARAM = 'preview';
/** Longest the app start waits for branding before painting with platform defaults. */
const FIRST_LOAD_TIMEOUT_MS = 1500;

export interface BrandingPreviewState {
	tenantId: string;
	tenantName: string;
}

/**
 * Runtime tenant branding.
 *
 * Follows the signed-in session: loads `GET /tenant-branding/me` on sign-in (and on start for a
 * stored session) and clears on sign-out. When nothing is published (204) it changes nothing —
 * no style element, title or favicon change — so un-branded tenants render exactly as before.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
	// Resolved lazily so components that only read branding (logo, topbar) don't pull in HttpClient.
	private readonly injector = inject(Injector);
	private get api(): BrandingApiService {
		return this.injector.get(BrandingApiService);
	}
	private get auth(): AuthService {
		return this.injector.get(AuthService);
	}
	private readonly permissions = inject(PermissionService);
	private readonly theme = inject(ThemeService);
	private readonly title = inject(Title);
	private readonly document = inject(DOCUMENT);
	private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));

	private readonly config = signal<BrandingConfig | null>(null);
	/** Branding currently applied, with platform defaults filling every gap. */
	readonly resolved = computed<ResolvedBranding>(() => {
		const resolved = resolveBranding(this.config());
		const api = environment.apiBaseUrl;
		return {
			...resolved,
			favicon: resolveBrandingAssetUrl(resolved.favicon, api),
			wordmark: {
				light: resolveBrandingAssetUrl(resolved.wordmark.light, api),
				dark: resolveBrandingAssetUrl(resolved.wordmark.dark, api),
			},
			mark: {
				light: resolveBrandingAssetUrl(resolved.mark.light, api),
				dark: resolveBrandingAssetUrl(resolved.mark.dark, api),
			},
		};
	});
	readonly appName = computed(() => this.resolved().appName);
	readonly brandName = computed(() => this.resolved().brandName);
	readonly footerText = computed(() => this.resolved().footerText);
	readonly isBranded = computed(() => this.config() !== null);
	/** Set while an admin previews another tenant's draft with `?preview=<tenantId>`. */
	readonly preview = signal<BrandingPreviewState | null>(null);

	/** Draft colors from the branding editor, layered over the applied branding for this session only. */
	private liveColors: BrandingConfig['colors'] | null = null;
	private defaultTitle: string | null = null;
	private defaultFavicon: string | null = null;
	/** `undefined` until the first session emission, so a signed-out start is still handled. */
	private sessionKey: string | null | undefined = undefined;
	private loadSub: Subscription | null = null;
	private initialized = false;

	/** App initializer. Resolves after the first load, or after a short timeout so start-up never waits long. */
	init(): Promise<void> {
		if (!this.browser || this.initialized) return Promise.resolve();
		this.initialized = true;
		this.defaultTitle = this.document.title;
		this.defaultFavicon = this.faviconLink()?.getAttribute('href') ?? null;
		const requestedPreview = this.readPreviewParam();

		let markReady: () => void = () => undefined;
		const firstLoad = new Promise<void>(resolve => (markReady = resolve));

		this.auth.currentUser$.subscribe(user => {
			const key = user ? `${user.id}|${user.tenantId ?? ''}` : null;
			if (key === this.sessionKey) return;
			this.sessionKey = key;
			if (!user) {
				this.preview.set(null);
				this.clear();
				markReady();
				return;
			}
			if (requestedPreview && !this.preview() && this.permissions.has('BRANDING_VIEW')) {
				this.preview.set({ tenantId: requestedPreview, tenantName: '' });
			}
			this.load().then(markReady);
		});

		return Promise.race([firstLoad, new Promise<void>(resolve => setTimeout(resolve, FIRST_LOAD_TIMEOUT_MS))]);
	}

	/** Re-read branding for the session (own tenant, or the previewed tenant). Errors keep what is shown. */
	load(): Promise<void> {
		this.loadSub?.unsubscribe();
		const preview = this.preview();
		const source$: Observable<BrandingConfig | null> = preview ? this.previewSource(preview.tenantId) : this.api.me().pipe(map(me => me?.branding ?? null));
		return new Promise(resolve => {
			this.loadSub = source$
				.pipe(
					map(branding => ({ branding })),
					catchError(() => of(null)),
				)
				.subscribe(result => {
					if (result) this.apply(result.branding);
					resolve();
				});
		});
	}

	/** Admin preview of a tenant's draft (falls back to its published branding). Needs BRANDING_VIEW. */
	startPreview(tenantId: string, tenantName = ''): Promise<void> {
		if (!this.permissions.has('BRANDING_VIEW') || !tenantId.trim()) return Promise.resolve();
		this.preview.set({ tenantId: tenantId.trim(), tenantName });
		return this.load();
	}

	exitPreview(): Promise<void> {
		this.preview.set(null);
		this.removePreviewParam();
		return this.load();
	}

	/** Apply a branding document. `null` clears back to platform defaults. */
	apply(branding: BrandingConfig | null): void {
		if (!this.browser) return;
		if (!branding) {
			this.clear();
			return;
		}
		this.config.set(branding);
		this.writeStyles();

		const appTitle = branding.identity?.appTitle?.trim();
		this.title.setTitle(appTitle || this.defaultTitle || this.document.title);
		const favicon = branding.logo?.favicon?.trim();
		this.setFavicon(favicon ? resolveBrandingAssetUrl(favicon, environment.apiBaseUrl) : this.defaultFavicon);
	}

	/**
	 * Show draft colors across the whole app (branding editor), for this browser session only.
	 * `null` goes back to the applied branding. Nothing is saved or shown to other users.
	 */
	previewColors(colors: BrandingConfig['colors'] | null): void {
		if (!this.browser) return;
		this.liveColors = colors;
		this.writeStyles();
	}

	/** Remove every trace of tenant branding. */
	clear(): void {
		if (!this.browser) return;
		const wasBranded = this.config() !== null || !!this.styleElement(false);
		this.config.set(null);
		if (!wasBranded && !this.liveColors) return;
		this.writeStyles();
		if (!wasBranded) return;
		if (this.defaultTitle !== null) this.title.setTitle(this.defaultTitle);
		this.setFavicon(this.defaultFavicon);
	}

	private writeStyles(): void {
		const applied = this.config();
		const doc: BrandingConfig | null = this.liveColors ? { ...(applied ?? { schemaVersion: 1 }), colors: this.liveColors } : applied;
		const css = buildBrandingCss(doc);
		if (css) this.styleElement(true)!.textContent = css;
		else this.styleElement(false)?.remove();
		this.theme.refreshContrastTokens();
	}

	private previewSource(tenantId: string): Observable<BrandingConfig | null> {
		return this.api.getDraft(tenantId, true).pipe(
			switchMap(draft => (draft ? of(draft) : this.api.getActiveQuiet(tenantId))),
			map(row => {
				const current = this.preview();
				if (current && row?.tenantName && !current.tenantName) this.preview.set({ ...current, tenantName: row.tenantName });
				return row?.branding ?? null;
			}),
		);
	}

	private styleElement(create: boolean): HTMLStyleElement | null {
		const existing = this.document.getElementById(BRANDING_STYLE_ID) as HTMLStyleElement | null;
		if (existing || !create) return existing;
		const style = this.document.createElement('style');
		style.id = BRANDING_STYLE_ID;
		this.document.head.appendChild(style);
		return style;
	}

	private faviconLink(): HTMLLinkElement | null {
		return this.document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
	}

	private setFavicon(href: string | null): void {
		const link = this.faviconLink();
		if (!link || href === null) return;
		if (link.getAttribute('href') !== href) link.setAttribute('href', href);
	}

	private readPreviewParam(): string {
		try {
			return new URL(this.document.location.href).searchParams.get(BRANDING_PREVIEW_PARAM)?.trim() ?? '';
		} catch {
			return '';
		}
	}

	private removePreviewParam(): void {
		try {
			const url = new URL(this.document.location.href);
			if (!url.searchParams.has(BRANDING_PREVIEW_PARAM)) return;
			url.searchParams.delete(BRANDING_PREVIEW_PARAM);
			this.document.defaultView?.history.replaceState(this.document.defaultView.history.state, '', url.pathname + url.search + url.hash);
		} catch {
			/* not a browser URL */
		}
	}
}
